import type { BaseProps } from '~/src/shared/helpers/withServerSidePropsDecorator'

/**
 * Single source of truth for the LaunchDarkly context attached to a base.
 * Used by both server-side evals (withServerSideProps, page-level redirects)
 * and the client-side identify call.
 *
 * If a new custom attribute is added (e.g. plan, region), do it here — both
 * sides pick it up automatically and drift is impossible.
 *
 * Callers that need a user-keyed context (e.g. non-mimicking agents) can
 * spread the result and override `key`, or call buildBaseCustomAttributes
 * directly for just the `custom` block.
 *
 * Client-safe: no server-only imports.
 */

const LD_SERVICE_NAME = 'venue-ark-frontend'

type BaseForLDContext = Pick<
  BaseProps,
  'baseXRefID' | 'corporationXRefID' | 'customerTier' | 'isBeta' | 'isTest'
>

// Matches LaunchDarkly's accepted `custom` shape on FeatureFlagUser.
type BaseLDCustomAttributes = {
  [key: string]: string | number | boolean | (string | number | boolean)[]
}

type BaseLDContext = {
  anonymous: boolean
  key: string
  custom: BaseLDCustomAttributes
}

export const buildBaseCustomAttributes = (
  base: BaseForLDContext | null | undefined
): BaseLDCustomAttributes => {
  const custom: BaseLDCustomAttributes = { service: LD_SERVICE_NAME }
  if (base?.corporationXRefID != null) {
    custom.corporationXRefID = base.corporationXRefID
  }
  if (base?.customerTier != null) {
    custom.customerTier = base.customerTier
  }
  if (base?.isBeta != null) {
    custom.isBeta = base.isBeta
  }
  if (base?.isTest != null) {
    custom.isTest = base.isTest
  }
  return custom
}

export const buildBaseLDContext = (base: BaseForLDContext): BaseLDContext => ({
  anonymous: false,
  key: `baseXRefID-${base.baseXRefID}`,
  custom: buildBaseCustomAttributes(base),
})
